import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  formatNumber,
  truncateToTwoDecimals,
} from "../../../../../utility/helper";
import { wealthAssetTypeOption, sectorss } from "../../../../../utility/data";
// import moment from "moment";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { roi, date } = payload[0].payload;
    const roiPercentage = truncateToTwoDecimals(roi);

    return (
      <div
        style={{
          padding: "10px",
          color: "#333",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>ROI:</strong>
          <p style={{ color: "#333" }}>{roiPercentage}%</p>
        </div>
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Date:</strong>
          <p style={{ color: "#333" }}>{date}</p>
        </div>
      </div>
    );
  }
  return null;
};

const RechartExample = ({ graphData, fortuneData, theme, groupsData }) => {
  const [activeIndex, setActiveIndex] = useState({});
  const [chartsData, setChartsData] = useState([]);

  useEffect(() => {
    if (fortuneData) {
      const getMappedName = (list, key) =>
        list?.find((item) => item.value === key)?.name || key;
      setChartsData([
        {
          title: "Sector",
          data:
            fortuneData.pie_sector_data?.map((item) => ({
              name: item.sector,
              value:
                item.percentage != null
                  ? parseFloat(Number(item.percentage).toFixed(2))
                  : 0,
              displayName: getMappedName(sectorss, item.sector),
            })) || [],
          colors: [
            "#FF85B6",
            "#57FFFF",
            "#FFBF00",
            "#007BFF",
            "#1ABC9C",
            "#FF4500",
            "#32CD32",
            "#8A2BE2",
            "#FFD700",
            "#DC143C",
            "rgb(133, 220, 20)",
            "rgb(220, 20, 170)",
          ],
        },
        {
          title: "Structure",
          data:
            fortuneData.pie_structure_data?.map((item) => ({
              name: `Group ${item.group}`,
              value:
                item.percentage != null
                  ? parseFloat(Number(item.percentage).toFixed(2))
                  : 0,
              displayName: getMappedName(
                groupsData?.map((g) => ({
                  name: g.name,
                  value: `Group ${g.id}`,
                })),
                `Group ${item.group}`
              ),
            })) || [],
          colors: [
            "#FFBF00",
            "#FF85B6",
            "#57FFFF",
            "#32CD32",
            "#8A2BE2",
            "#FF4500",
          ],
        },
        {
          title: "Allocation",
          data:
            fortuneData.pie_allocation_data?.map((item) => ({
              name: item.asset_class,
              value:
                item.percentage != null
                  ? parseFloat(Number(item.percentage).toFixed(2))
                  : 0,
              displayName: getMappedName(
                wealthAssetTypeOption,
                item.asset_class
              ),
            })) || [],
          colors: ["#FF85B6", "#57FFFF", "#FFBF00", "#007BFF", "#1ABC9C"],
        },
      ]);
    }
  }, [fortuneData, groupsData]);

  const handleHover = (chartIndex, index) => {
    setActiveIndex((prev) => {
      const newState = { ...prev };
      if (typeof index === "number") {
        newState[chartIndex] = index;
      } else {
        delete newState[chartIndex];
      }
      return newState;
    });
  };

  return (
    <div className="flex gap-4">
      <div className="w-full">
        <div className="lg:w-2/3 w-full overflow-x-auto lg:ml-14 my-10">
          <table className="min-w-[670px] text-base border-collapse text-info dark:text-grey">
            <thead>
              <tr>
                <th className="text-left pr-4"></th>
              </tr>
            </thead>
            <tbody className="font-semibold">
              <tr>
                <td className="pb-2">Your fortune is now valued a</td>
                <td
                  className={`pb-2 ${
                    parseFloat(fortuneData?.fortune ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-lightPrimary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(Number(fortuneData?.fortune ?? "0"))
                  )}{" "}
                  CHF
                </td>
              </tr>
              <tr>
                <td className="pb-2">
                  The fortune has{" "}
                  {parseFloat(fortuneData?.fortune_increased ?? 0) < 0
                    ? "decreased"
                    : "increased"}{" "}
                  by
                </td>
                <td
                  className={`pb-2 px-2 ${
                    parseFloat(fortuneData?.fortune_increased ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-lightPrimary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(
                      Number(fortuneData?.fortune_increased ?? "0")
                    )
                  )}{" "}
                  CHF
                </td>
                <td className="pb-2">Year to date</td>
              </tr>
              <tr>
                <td className="">
                  Due to total{" "}
                  {parseFloat(fortuneData?.total_profit ?? 0) < 0
                    ? "loss"
                    : "profit"}{" "}
                  of
                </td>
                <td
                  className={`${
                    parseFloat(fortuneData?.total_profit ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-lightPrimary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(
                      Number(fortuneData?.total_profit ?? "0")
                    )
                  )}{" "}
                  CHF
                </td>
                <td
                  className={`text-center ${
                    parseFloat(fortuneData?.percentage_value ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-lightPrimary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(
                      Number(fortuneData?.percentage_value ?? 0)
                    )
                  )}{" "}
                  %
                </td>

                <td className="">and a net flow of</td>
                <td
                  className={`${
                    parseFloat(fortuneData?.net_flow ?? 0) < 0
                      ? "text-expense dark:text-warning"
                      : "text-lightPrimary dark:text-primary-gradient"
                  }`}
                >
                  {formatNumber(
                    truncateToTwoDecimals(
                      Math.abs(Number(fortuneData?.net_flow ?? 0))
                    )
                  )}{" "}
                  CHF
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <ResponsiveContainer height={400} className="p-2">
          <LineChart data={fortuneData?.graph_data}>
            <defs>
              {theme === "light" ? (
                <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#72b4eb" />
                  <stop offset="40%" stopColor="#57acf1" />
                  <stop offset="100%" stopColor="#47C1F0" />
                </linearGradient>
              ) : (
                <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#cce9ff" />
                  <stop offset="40%" stopColor="rgb(130, 188, 235)" />
                  <stop offset="60%" stopColor="#6eb6f1" />
                  <stop offset="80%" stopColor="#3a91d9" />
                  <stop offset="100%" stopColor="#47C1F0" />
                </linearGradient>
              )}
            </defs>

            <CartesianGrid strokeDasharray="0 1" stroke="#ccc" />
            <XAxis
              dataKey="date"
              tick={({ x, y, payload }) => (
                <text
                  x={x - 30}
                  y={y + 10}
                  fill={
                    theme === "dark" ? "rgb(206, 206, 206)" : "rgb(77, 76, 83)"
                  }
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                >
                  {""}
                </text>
              )}
              axisLine={false}
              tickFormatter={(value) => value && ""}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={({ x, y, payload }) => (
                <text
                  x={x - 45}
                  y={y}
                  fill={
                    theme === "dark" ? "rgb(206, 206, 206)" : "rgb(77, 76, 83)"
                  }
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                >
                  {payload.value}%
                </text>
              )}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Line
              type="linear"
              dataKey="roi"
              stroke="url(#colorGradient)"
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex md:justify-center font-semibold items-center text-center text-info dark:text-grey md:flex-row flex-col md:gap-28 gap-4 mt-2">
          <div className="flex flex-col gap-1">
            <span className="">unrealized profit</span>
            <span
              className={`${
                parseFloat(fortuneData?.unrealized ?? 0) < 0
                  ? "text-expense dark:text-warning"
                  : "text-lightPrimary dark:text-primary-gradient"
              }`}
            >
              {fortuneData?.unrealized
                ? `${formatNumber(
                    truncateToTwoDecimals(Number(fortuneData?.unrealized))
                  )} CHF`
                : "0 CHF"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="">Sale</span>
            <span className="text-lightPrimary dark:text-primary-gradient">
              {`${
                fortuneData?.realized
                  ? formatNumber(
                      truncateToTwoDecimals(Number(fortuneData.realized))
                    )
                  : "0"
              } CHF`}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="">Income</span>
            <span className="text-lightPrimary dark:text-primary-gradient">
              {`${
                fortuneData?.income
                  ? formatNumber(
                      truncateToTwoDecimals(Number(fortuneData.income))
                    )
                  : "0"
              } CHF`}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="">Expense</span>
            <span className="text-expense dark:text-warning">
              {`${
                fortuneData?.expense
                  ? formatNumber(
                      truncateToTwoDecimals(Number(fortuneData.expense))
                    )
                  : "0"
              } CHF`}
            </span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:justify-around gap-10 mt-16">
          {chartsData.map((chart, chartIndex) => (
            <div key={chartIndex} className="text-center w-full md:w-1/3">
              <h3 className="text-lightPrimary dark:text-grey font-bold mb-2">
                {chart.title}
              </h3>
              <ResponsiveContainer height={200}>
                <PieChart>
                  <Tooltip
                    cursor={{ fill: "none" }}
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;
                      const data = payload[0]?.payload;
                      return (
                        <div className="bg-white text-info p-4 rounded shadow-md text-sm">
                          <strong>
                            {data?.displayName || data?.name}: {data?.value}%
                          </strong>
                        </div>
                      );
                    }}
                  />
                  <Pie
                    data={chart.data}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    activeShape={{ outerRadius: 80 }}
                    onMouseEnter={(_, index) => handleHover(chartIndex, index)}
                    onMouseLeave={() => handleHover(chartIndex)}
                    activeIndex={
                      activeIndex[chartIndex] !== undefined
                        ? activeIndex[chartIndex]
                        : null
                    }
                  >
                    {chart.data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chart.colors[index]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-3 justify-center flex-wrap">
                {chart.data.map((entry, index) => {
                  return (
                    <div
                      key={index}
                      className="md:flex gap-2 items-center mb-2"
                    >
                      <span
                        className="hidden md:block"
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: chart.colors[index],
                          borderRadius: 2,
                        }}
                      ></span>
                      <span className="text-lightPrimary dark:text-grey text-sm">
                        <span className="text-lightPrimary dark:text-grey text-sm">
                          {entry.displayName || entry.name}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RechartExample;
